import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ReactComponent as PickitLogoMobile } from '../assets/pickit-logo-mobile.svg';
import { ReactComponent as PickitLogo } from '../assets/pickit_logo_white.svg';
import { ReactComponent as PickersLogo } from '../assets/pickers_logo_white.svg';
import BasicIcon from '../BasicIcon/BasicIcon';
import StaticIcon from '../StaticIcon/StaticIcon';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { disableScrollBody, enableScrollBody } from '../utils/scroll';
import ContextMenu from './components/ContextMenu/ContextMenu';
import Notifications from './components/Notifications/Notifications';
import {
  BlurContainer,
  FlagsContainer,
  MenuIconContainer,
  MiddleIcon,
  NavBarAnchor,
  NavBarContainer,
  NavBarSpacer,
  NotificationContainer,
  SectionContainer,
  ToggleFlag,
  UserContainer,
} from './styles';
import { NavbarProps } from './types';
import { ThemeEnum } from '../ThemeProvider/types';
import FlagsDropdown, { getFlag } from './components/FlagsDropdown/FlagsDropdown';

const NavBar: React.FC<NavbarProps> = ({
  contextMenu,
  isMobile,
  logoOnClick,
  status = 'default',
  testId,
  children,
  notifications,
  labelReadAll = 'Marcar como leídas',
  labelReadMore = 'Leer menos',
  labelReadLess = 'Leer más',
  titleEmptyNotification = 'No hay notificaciones nuevas.',
  subtitleEmptyNotification = 'Cuando sean enviadas podrás visualizarlas aquí.',
  readAll,
  closeOnBlurNotification = true,
  countryCode,
  setCountryCode,
  countries,
  version,
  labelVersion,
  keepOpen = false,
}) => {
  const { actualTheme, isMobile: mobileTheme, themeName } = useContext(ThemeContext);
  const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);
  const [sideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [notificationOpen, setNotificationOpen] = useState<boolean>(false);
  const [showFlags, toggleFlags] = useState<boolean>(false);
  const useMobileVersion = useMemo(() => (isMobile !== undefined ? isMobile : mobileTheme), [
    isMobile,
    mobileTheme,
  ]);
  const CONTEXT_MENU = 'context';
  const SIDEBAR = 'sidebar';
  const NOTIFICATION = 'notification';
  const COUNTRY = 'country';

  const handleIsState = useMemo((): boolean => contextMenuOpen || sideBarOpen || notificationOpen, [
    contextMenuOpen,
    notificationOpen,
    sideBarOpen,
  ]);

  useEffect(() => {
    if (handleIsState) {
      disableScrollBody();
    } else {
      enableScrollBody();
    }
  }, [handleIsState]);

  const handleToggle = useCallback(
    (name?: string) => (): void => {
      setContextMenuOpen((value) => (name === CONTEXT_MENU ? !value : false));
      setNotificationOpen((value) => (name === NOTIFICATION ? !value : false));
      setSideBarOpen((value) => (name === SIDEBAR ? !value : false));
      toggleFlags((value) => (name === COUNTRY ? !value : false));
    },
    [],
  );

  const globalClose = useCallback(
    (e) => {
      e.stopPropagation();
      toggleFlags(false);
      window.removeEventListener('click', globalClose);
    },
    [toggleFlags],
  );

  const handleClick = useCallback(() => {
    handleToggle()();
    if (logoOnClick) logoOnClick();
  }, [handleToggle, logoOnClick]);

  const handleIconNotification = useMemo(() => {
    const isRead = notifications?.some((e) => !e?.read);
    return <StaticIcon name={isRead ? 'bell_notif' : 'bell'} />;
  }, [notifications]);

  useEffect(() => {
    if (showFlags) window.addEventListener('click', globalClose);
  }, [globalClose, showFlags]);

  return (
    <ThemeProvider theme={actualTheme}>
      <BlurContainer onClick={handleToggle()} isStateOpen={handleIsState} />
      <NavBarAnchor>
        <NavBarContainer status={status} data-testid={testId} isMobile={useMobileVersion}>
          {React.Children.toArray(children).length > 0 && (
            <MenuIconContainer
              onClick={handleToggle(SIDEBAR)}
              data-testId={`${testId}-sidebar-toggle`}
            >
              <BasicIcon name="menu" color="white" size="large" clickable />
              {!useMobileVersion && <span>MENÚ</span>}
            </MenuIconContainer>
          )}
          <MiddleIcon onClick={handleClick} data-testId={`${testId}-logo`}>
            {
              // eslint-disable-next-line no-nested-ternary
              useMobileVersion ? (
                <PickitLogoMobile />
              ) : themeName === ThemeEnum.pickers ? (
                <PickersLogo />
              ) : (
                <PickitLogo />
              )
            }
          </MiddleIcon>
          <SectionContainer>
            {countryCode && (
              <FlagsContainer>
                <ToggleFlag onClick={handleToggle(COUNTRY)}>
                  <StaticIcon
                    size="medium"
                    name={getFlag(countryCode)}
                    clickable={!!setCountryCode}
                    testId={`${testId}-actual-flag`}
                  />
                </ToggleFlag>
                {setCountryCode && (
                  <FlagsDropdown
                    testId={testId}
                    open={showFlags}
                    changeCountry={setCountryCode}
                    actualCountry={countryCode}
                    countries={countries}
                    onClose={handleToggle(COUNTRY)}
                  />
                )}
              </FlagsContainer>
            )}
            <NotificationContainer onClick={handleToggle(NOTIFICATION)}>
              {notifications && handleIconNotification}
            </NotificationContainer>
            <UserContainer
              onClick={handleToggle(CONTEXT_MENU)}
              data-testId={`${testId}-context-menu-toggle`}
            >
              <StaticIcon name={actualTheme.userLogoName} clickable />
            </UserContainer>
          </SectionContainer>
          <ContextMenu
            isOpen={contextMenuOpen}
            contextMenu={contextMenu}
            onClose={handleToggle(CONTEXT_MENU)}
            testId={`${testId}-context-menu`}
            version={version}
            labelVersion={labelVersion}
          />
          <Notifications
            isOpen={notificationOpen}
            onClose={handleToggle(NOTIFICATION)}
            notifications={notifications}
            isMobile={isMobile}
            titleNotifications="Notificaciones"
            labelReadAll={labelReadAll}
            labelReadMore={labelReadMore}
            labelReadLess={labelReadLess}
            titleEmptyNotification={titleEmptyNotification}
            subtitleEmptyNotification={subtitleEmptyNotification}
            readAll={readAll}
            closeOnBlur={closeOnBlurNotification}
          />
          {React.Children.map(children, (child) => {
            // Checking isValidElement is the safe way and avoids a typescript
            // error too.
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                isMobile: useMobileVersion,
                isOpen: sideBarOpen,
                onClose: handleToggle(SIDEBAR),
                keepOpen,
              });
            }
            return child;
          })}
        </NavBarContainer>
      </NavBarAnchor>
      <NavBarSpacer isMobile={useMobileVersion} />
    </ThemeProvider>
  );
};

export default NavBar;
